/**
 * KAN-755: форма скидки: название, тип (из реестра GET /discount-types/,
 * описание под выбором; при правке тип не меняется), параметры типа,
 * включена или выключена, на что действует. Ошибки 400 по полям.
 * Логика: useDiscountForm.
 */
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { FormField } from '@/components/catalog/FormField';
import { useDiscountForm } from '@/hooks/useDiscountForm';
import { DiscountParamsFields } from './DiscountParamsFields';
import { ScopePicker } from './ScopePicker';

interface DiscountFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Скидка для правки; null: новая. */
  discountId: string | null;
}

export function DiscountFormDialog({ open, onOpenChange, discountId }: DiscountFormDialogProps) {
  const form = useDiscountForm(open, discountId);
  const { values, errors, pending } = form;
  const editing = discountId !== null;

  const submit = async (): Promise<void> => {
    const saved = await form.submit();
    if (saved) onOpenChange(false);
  };

  let body: JSX.Element;
  if (form.loadError) {
    body = <p className="text-sm text-red-700">Не удалось загрузить скидку</p>;
  } else if (form.loading) {
    body = <p className="text-sm text-gray-500">Загрузка...</p>;
  } else {
    body = (
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField id="discount-name" label="Название *" error={errors.name}>
          <Input
            id="discount-name"
            value={values.name}
            maxLength={255}
            autoComplete="off"
            disabled={pending}
            aria-invalid={errors.name ? true : undefined}
            onChange={(e) => form.setName(e.target.value)}
          />
        </FormField>
        <FormField
          id="discount-formula"
          label="Тип скидки *"
          error={errors.formula}
          hint={form.type?.description || undefined}
        >
          <NativeSelect
            id="discount-formula"
            options={form.types.map((t) => ({ value: t.code, label: t.label }))}
            value={values.formula}
            disabled={pending || editing}
            onChange={(e) => form.setFormula(e.target.value)}
          />
        </FormField>
        {form.type && (
          <DiscountParamsFields
            specs={form.type.params}
            values={values.params}
            onChange={form.setParam}
            errors={errors}
            disabled={pending}
          />
        )}
        <div className="flex items-center gap-2">
          <Switch
            id="discount-active"
            checked={values.isActive}
            disabled={pending}
            onCheckedChange={form.setActive}
            data-testid="discount-active"
          />
          <Label htmlFor="discount-active">{values.isActive ? 'Включена' : 'Выключена'}</Label>
        </div>
        <ScopePicker value={values} onChange={form.setScope} error={errors.scope} disabled={pending} />
        {form.formError && (
          <p className="text-sm text-red-700" role="alert" data-testid="discount-form-error">
            {form.formError}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Отмена
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </form>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="discount-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Изменить скидку' : 'Новая скидка'}</DialogTitle>
          <DialogDescription>Если на товар действует несколько скидок, применяется самая выгодная.</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
